/**
 * React Query hooks for Sync API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { syncApi } from '../services/sync';
import { activityLogKeys } from './useActivityLogs';
import { reportsKeys } from './useReports';

// =============================================================================
// Query Keys
// =============================================================================

export const syncKeys = {
  all: ['sync'] as const,
  logs: (projectId: string) => [...syncKeys.all, 'logs', projectId] as const,
};

// =============================================================================
// Queries
// =============================================================================

/**
 * Hook to fetch sync activity logs for a project
 */
export function useSyncLogs(projectId: string) {
  return useQuery({
    queryKey: syncKeys.logs(projectId),
    queryFn: () => syncApi.getSyncLogs(projectId),
    enabled: !!projectId,
    staleTime: 30000,
  });
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Hook to trigger a manual sync for a project.
 * Invalidates report and sync log caches on success.
 */
export function useTriggerSync(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (options?: { force?: boolean }) => syncApi.triggerProjectSync(projectId, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportsKeys.projectAll(projectId) });
      queryClient.invalidateQueries({ queryKey: syncKeys.logs(projectId) });
      queryClient.invalidateQueries({ queryKey: activityLogKeys.mapping(projectId) });
    },
    onError: (error: Error) => {
      toast.error(`Sync failed: ${error.message}`);
    },
  });
}

/**
 * Hook to recreate Graph webhook subscriptions for a project.
 * Invalidates sync logs and project report caches on success.
 */
export function useRecreateSubscriptions(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => syncApi.recreateSubscriptions(projectId, { force_new: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportsKeys.projectAll(projectId) });
      queryClient.invalidateQueries({ queryKey: syncKeys.logs(projectId) });
    },
    onError: (error: Error) => {
      toast.error(`Subscription recreation failed: ${error.message}`);
    },
  });
}
